import { Injectable } from '@angular/core';
import { Subject, Subscription, Observable, interval } from 'rxjs';
import { switchMap, map, filter } from 'rxjs/operators';
import { IssueOrderPeriod } from '../../helpers/issue/order-period';
import { toObservable } from '../../utils/converters/observable';

export type DealToCountdownMapper<U> = (
  deal: U
) => IssueOrderPeriod.OrderPeriodCountdown;

@Injectable()
export class OrderPeriodCountdownService<U> {
  private intervalSubscription: Subscription;

  private readonly countdownSubj: Subject<IssueOrderPeriod.OrderPeriodCountdown> = new Subject();

  private readonly countdown$: Observable<IssueOrderPeriod.OrderPeriodCountdown> = this.countdownSubj.pipe(
    filter((countdown: IssueOrderPeriod.OrderPeriodCountdown) => !!countdown)
  );

  public subscribe(
    deal: U | Observable<U>,
    mapper: DealToCountdownMapper<U>
  ): Observable<IssueOrderPeriod.OrderPeriodCountdown> {
    if (!this.intervalSubscription || this.intervalSubscription.closed) {
      this.intervalSubscription = this.startInterval(
        toObservable(deal),
        mapper
      ).subscribe({
        next: (orderPeriod: IssueOrderPeriod.OrderPeriodCountdown) =>
          this.countdownSubj.next(orderPeriod)
      });
    }

    return this.countdown$;
  }

  public unsubscribe(): void {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
  }

  private startInterval(
    deal$: Observable<U>,
    mapper: DealToCountdownMapper<U>
  ): Observable<IssueOrderPeriod.OrderPeriodCountdown> {
    return interval(500).pipe(
      switchMap(() => deal$),
      map(mapper)
    );
  }
}
